/* ============================================================================
   COMMUNICATION LOG — every email the studio and the client exchanged on the
   record: what went, to whom, when, and whether it was delivered. The
   contractual trail, in one place, with the day-by-day activity under it.
   ========================================================================== */
import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead } from "../components/ui";
import { Icon } from "../components/Icons";
import { recordState } from "../components/RecordStatus";
import { fmtDate } from "../lib/format";
import { RECORD_LABEL } from "../lib/types";
import type { RecordKind } from "../lib/types";

const GROUP: Record<string, string> = {
  "invoice-issued": "Billing", "invoice-reminder": "Billing", "statement-issued": "Billing", "certificate-issued": "Billing", "payment-received": "Billing",
  "engagement-issued": "Engagement", "engagement-signed": "Engagement", "brief-approved": "Brief",
  "approval-issued": "Drawings", "approval-signed": "Drawings", "approval-declined": "Drawings",
  "activity-logged": "Activity", "daily-log": "Daily log", "weekly-update": "Weekly update",
};

export default function Records() {
  const p = useCurrent();
  const [group, setGroup] = useState("all");
  const [q, setQ] = useState("");
  if (!p) return null;
  const rows = [...(p.records ?? [])].sort((a, b) => b.on.localeCompare(a.on));
  const groups = [...new Set(rows.map((r) => GROUP[r.kind] ?? "Other"))];
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => (group === "all" || (GROUP[r.kind] ?? "Other") === group) && (!needle || `${r.title} ${RECORD_LABEL[r.kind as RecordKind] ?? r.kind} ${r.recipients.map((x) => x.to).join(" ")}`.toLowerCase().includes(needle)));
  const failed = rows.filter((r) => r.recipients.some((x) => x.result === "failed")).length;
  return (
    <Page>
      <PageHead kicker={`${p.name} · ${rows.length} on record${failed ? ` · ${failed} with a failed delivery` : ""}`} title="Communication log" />
      <p className="dim reveal" style={{ fontSize: 12.5, marginBottom: 16, maxWidth: 720, lineHeight: 1.6 }}>
        Every formal email sent on this project — invoices, bills, payments, submissions, approvals, the daily activity log and the weekly update — with who received it and whether it was delivered. Both parties get the same email; this is the studio's copy of the trail.
      </p>
      <div className="row wrap reveal" style={{ gap: 8, marginBottom: 14, alignItems: "center" }}>
        <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find — title, recipient" style={{ flex: 1, minWidth: 200, maxWidth: 360 }} />
        <button className={"chip" + (group === "all" ? " on" : "")} onClick={() => setGroup("all")}>All · {rows.length}</button>
        {groups.map((g) => <button key={g} className={"chip" + (group === g ? " on" : "")} onClick={() => setGroup(g)}>{g} · {rows.filter((r) => (GROUP[r.kind] ?? "Other") === g).length}</button>)}
      </div>
      {shown.length === 0 && <div className="card pad reveal dim" style={{ fontSize: 13 }}>{rows.length ? "Nothing matches." : "Nothing on the record yet. The first formal email on this project will appear here."}</div>}
      <div className="card reveal" style={{ overflow: "hidden" }}>
        {shown.map((r, i) => {
          const st = recordState(r);
          return (
            <div key={r.id} className="row between wrap" style={{ padding: "12px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10, alignItems: "flex-start" }}>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="row wrap" style={{ gap: 8, alignItems: "baseline" }}>
                  <span className="badge" style={{ fontSize: 9 }}>{RECORD_LABEL[r.kind as RecordKind] ?? r.kind}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{r.title}</span>
                </span>
                <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 3 }}>
                  {fmtDate(r.on)} · by {r.by} · {r.recipients.map((x) => `${x.party}: ${x.to || "no address"} — ${x.result}${x.error ? ` (${x.error})` : ""}`).join(" · ")}
                  {r.attachment && <> · <a href={r.attachment.url} target="_blank" rel="noreferrer"><Icon.download width={11} style={{ verticalAlign: "-1px" }} /> {r.attachment.name}</a></>}
                </span>
              </span>
              <span className={`badge ${st.tone}`} style={{ fontSize: 9.5, flexShrink: 0 }}>{st.label}</span>
            </div>
          );
        })}
      </div>
    </Page>
  );
}
